import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import FileUpload from '../components/FileUpload';
import api from '../config/api';

interface Job {
  id: number;
  title: string;
  description: string;
  job_type: string;
  location_type: string;
  salary: string;
  requirements: string;
  company: string;
  employer_name: string;
  employer_email: string;
  created_at: string;
}

const JobDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [job, setJob] = useState<Job | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [applicationData, setApplicationData] = useState({
    name: '',
    email: '',
    phone: '',
    coverLetter: '',
    resumeUrl: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    if (id) {
      fetchJob(id);
    }
  }, [id]);

  useEffect(() => {
    if (user) {
      setApplicationData(prev => ({
        ...prev,
        name: user.name,
        email: user.email
      }));
    }
  }, [user]);

  const fetchJob = async (jobId: string) => {
    try {
      const response = await api.get(`/api/jobs/${jobId}`);
      setJob(response.data);
    } catch (error) {
      console.error('Error fetching job:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !job) return;

    setIsSubmitting(true);
    setMessage('');

    try {
      let resumeUrl = '';
      
      // Upload resume if selected
      if (selectedFile) {
        const formData = new FormData();
        formData.append('resume', selectedFile);
        
        const uploadResponse = await api.post('/api/applications/upload-resume', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        resumeUrl = uploadResponse.data.resumeUrl;
      }

      await api.post('/api/applications', {
        jobId: job.id,
        jobSeekerId: user.id,
        ...applicationData,
        resumeUrl
      });

      setMessage('Application submitted successfully!');
      setShowApplicationForm(false);
      setApplicationData({
        name: '',
        email: '',
        phone: '',
        coverLetter: '',
        resumeUrl: ''
      });
      setSelectedFile(null);
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Failed to submit application');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
    setApplicationData(prev => ({
      ...prev,
      resumeUrl: URL.createObjectURL(file)
    }));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setApplicationData({
      ...applicationData,
      [e.target.name]: e.target.value
    });
  };

  if (isLoading) {
    return <div className="loading">Loading job details...</div>;
  }

  if (!job) {
    return <div className="error">Job not found</div>;
  }

  return (
    <div className="job-detail-container">
      <button onClick={() => navigate(-1)} className="btn btn-secondary">
        ← Back to Jobs
      </button>

      <div className="job-detail">
        <div className="job-header">
          <h1>{job.title}</h1>
          <div className="job-meta">
            <span className={`job-type ${job.job_type}`}>
              {job.job_type.replace('-', ' ')}
            </span>
            <span className={`location-type ${job.location_type}`}>
              {job.location_type}
            </span>
            {job.salary && <span className="salary">{job.salary}</span>}
          </div>
        </div>

        <div className="job-company">
          <h2>{job.company}</h2>
          <p>Posted by {job.employer_name}</p>
        </div>

        <div className="job-description">
          <h3>Job Description</h3>
          <p>{job.description}</p>
        </div>

        {job.requirements && (
          <div className="job-requirements">
            <h3>Requirements</h3>
            <p>{job.requirements}</p>
          </div>
        )}

        <div className="job-actions">
          {user ? (
            user.userType === 'jobseeker' ? (
              <button
                onClick={() => setShowApplicationForm(!showApplicationForm)}
                className="btn btn-primary"
              >
                Apply Now
              </button>
            ) : (
              <p className="note">You need to be logged in as a job seeker to apply.</p>
            )
          ) : (
            <p className="note">
              Please <a href="/login">login</a> or <a href="/register">register</a> to apply.
            </p>
          )}
        </div>

        {showApplicationForm && user && user.userType === 'jobseeker' && (
          <div className="application-form">
            <h3>Apply for {job.title}</h3>
            {message && <div className="message">{message}</div>}
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <FileUpload
                  onFileSelect={handleFileSelect}
                  label="Upload Resume (PDF only)"
                />
                {selectedFile && (
                  <div className="selected-file">
                    Selected: {selectedFile.name}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone (Optional)</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={applicationData.phone}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="coverLetter">Cover Letter</label>
                <textarea
                  id="coverLetter"
                  name="coverLetter"
                  value={applicationData.coverLetter}
                  onChange={handleInputChange}
                  rows={6}
                  required
                  placeholder="Tell us why you're interested in this position..."
                />
              </div>
              <div className="form-group">
                <label htmlFor="name">Full Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={applicationData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={applicationData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>

            

              

              <div className="form-actions">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Application'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowApplicationForm(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobDetail;
